"use client";

import { ReactNode, Ref } from "react";
import { ArrowRightToLine, PanelRight } from "lucide-react";
import { Button } from "@/components/shadcn/button";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarProvider,
} from "@/components/shadcn/sidebar";
import { NoxusTooltip } from "@/components/UI/NoxusTooltip";
import { cn } from "@/lib/utils";
import { SideNavAccountMenu } from "./SideNavAccountMenu";
import { CollapseBlockContext } from "./sidenavCollapseBlock";
import { revealHandlers, useSidenavOpenState } from "./useSidenavOpenState";
import {
  SIDENAV_FOOTER_CLASS,
  SIDENAV_HEADER_CLASS,
  SIDENAV_ICON_BUTTON_CLASS,
} from "./sidenavShared";

// The frame every sidenav shares: how it opens, collapses and pins, its
// header row (with the pin button), a scrolling body and the account menu.
export const SideNavShell = ({
  header,
  testIdPrefix,
  contentRef,
  contentClassName,
  children,
}: {
  // The header's left side: the workspace switcher, or a back arrow and title.
  header: ReactNode;
  testIdPrefix: string;
  contentRef?: Ref<HTMLDivElement>;
  contentClassName?: string;
  children: ReactNode;
}) => {
  const {
    attach,
    open,
    isCollapsible,
    setIsCollapsible,
    setIsHovered,
    setIsCollapseBlocked,
  } = useSidenavOpenState();
  const pinLabel = isCollapsible ? "Pin sidebar" : "Unpin sidebar";

  return (
    <CollapseBlockContext.Provider value={setIsCollapseBlocked}>
      {/* Above the page's own layers (sticky bars, selects); dialogs and
          popovers sit higher still. */}
      <div className="flex flex-col items-start w-fit h-full bg-sidebar z-40">
        <SidebarProvider open={open} onOpenChange={() => {}}>
          <Sidebar
            ref={attach}
            collapsible="icon"
            overlay={isCollapsible}
            className="z-40 max-w-full border-r border-border [&>[data-slot=sidebar-inner]]:overflow-hidden"
            {...revealHandlers(setIsHovered)}
          >
            <SidebarHeader className={SIDENAV_HEADER_CLASS}>
              <div className="flex items-center justify-between gap-1 h-8 shrink-0">
                {header}
                <NoxusTooltip content={pinLabel} asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => setIsCollapsible((prev) => !prev)}
                    aria-label={pinLabel}
                    data-testid={`${testIdPrefix}-pin-toggle`}
                    className={cn(
                      SIDENAV_ICON_BUTTON_CLASS,
                      "shrink-0 group-data-[collapsible=icon]:hidden"
                    )}
                  >
                    {isCollapsible ? (
                      <ArrowRightToLine className="size-4" strokeWidth={2} />
                    ) : (
                      <PanelRight className="size-4" strokeWidth={2} />
                    )}
                  </Button>
                </NoxusTooltip>
              </div>
            </SidebarHeader>
            <SidebarContent
              ref={contentRef}
              className={cn(
                "overflow-x-clip max-w-full gap-4 pt-4",
                contentClassName
              )}
            >
              {children}
            </SidebarContent>
            <SidebarFooter className={SIDENAV_FOOTER_CLASS}>
              <SidebarMenu className="list-none gap-1">
                <SideNavAccountMenu />
              </SidebarMenu>
            </SidebarFooter>
          </Sidebar>
        </SidebarProvider>
      </div>
    </CollapseBlockContext.Provider>
  );
};
